<template>
  <div class="inline-edit-container">
    <!-- Display Mode -->
    <transition name="edit-transition" mode="out-in" @after-enter="onTransitionComplete">
      <div
        v-if="!isEditing"
        key="display"
        class="option-display"
        @click="startEditing"
      >
        <span class="option-title">{{ displayTitle }}</span>
        <v-icon x-small class="edit-icon">mdi-pencil</v-icon>
      </div>

      <!-- Edit Mode -->
      <div
        v-else
        key="edit"
        class="option-edit"
        :class="{ 'is-saving': loading, 'save-success': showSuccess }"
      >
        <div class="input-wrapper">
          <input
            ref="titleInput"
            v-model="dsc_titulo"
            class="option-input"
            :disabled="loading"
            @keydown.enter="saveAndClose"
            @keydown.esc="cancelEditing"
            @blur="onBlur"
          />
          <div class="input-border"></div>
        </div>
        <div class="status-icon-container">
          <v-icon
            v-if="showSuccess"
            class="success-icon"
            color="primary"
            x-small
          >
            mdi-check-circle
          </v-icon>
          <v-progress-circular
            v-else-if="loading"
            indeterminate
            size="14"
            width="2"
            color="primary"
            class="loading-indicator"
          />
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { apiClient } from "@/services/api";
import { mapActions } from "vuex";

export default {
  props: {
    editFormData: Object,
  },
  data() {
    return {
      loading: false,
      dsc_titulo: "",
      originalTitle: "",
      isEditing: false,
      showSuccess: false,
      timeoutIds: [],
    };
  },
  computed: {
    displayTitle() {
      return this.editFormData?.dsc_titulo || "Título da opção";
    },
  },
  created() {
    if (this.editFormData !== null && this.editFormData !== undefined) {
      this.dsc_titulo = this.editFormData.dsc_titulo;
      this.originalTitle = this.editFormData.dsc_titulo;
    }
  },
  methods: {
    ...mapActions({
      getEnquetesProducao: "enquetes/getEnquetesProducao",
      getEnqueteEmExibicao: "enquetes/getEnqueteEmExibicao",
    }),
    startEditing() {
      this.originalTitle = this.dsc_titulo;
      this.isEditing = true;
      this.showSuccess = false;
    },
    onTransitionComplete() {
      // Called after the edit input transition completes
      if (this.isEditing && this.$refs.titleInput) {
        this.$refs.titleInput.focus();
      }
    },
    cancelEditing() {
      this.dsc_titulo = this.originalTitle;
      this.isEditing = false;
      this.loading = false;
      this.showSuccess = false;
    },
    onBlur() {
      const timeoutId = setTimeout(() => {
        if (this.isEditing && !this.loading && !this.showSuccess) {
          this.cancelEditing();
        }
      }, 150);
      this.timeoutIds.push(timeoutId);
    },
    async saveAndClose() {
      if (this.loading) return;

      if (this.dsc_titulo.trim() === this.originalTitle?.trim()) {
        this.isEditing = false;
        return;
      }

      if (this.dsc_titulo.trim() === "") {
        this.$toast.error("O texto não pode estar vazio.");
        this.dsc_titulo = this.originalTitle;
        this.isEditing = false;
        return;
      }

      await this.editarTextoOpcao();
    },
    async editarTextoOpcao() {
      try {
        this.loading = true;

        const payload = {
          dsc_titulo: this.dsc_titulo,
          url_imagem: this.editFormData.url_imagem,
          url_thumbnail: this.editFormData.url_thumbnail,
          dsc_nome_arquivo: this.editFormData.dsc_nome_arquivo,
          dsc_cor_barra: this.editFormData.dsc_cor_barra,
        };

        const cod_producao = this.$store.state.producoes.selectedProducao.cod_producao;

        const response = await apiClient.patch(
          `/producao/${cod_producao}/enquetes/${this.editFormData.id_enquete}/opcoes/${this.editFormData.id}`,
          payload
        );

        if (response.status >= 200 && response.status < 300) {
          this.showSuccess = true;

          await Promise.all([
            this.getEnquetesProducao({
              cod_producao: this.$store.state.producoes.selectedProducao.cod_producao,
              filtro_texto: this.$store.state.enquetes.filtroEnquetes,
              size: 5,
              page: this.$store.state.enquetes.enquetesProducao.currentPage,
              cod_portal: this.$store.state.enquetes.selectedPortal,
              id_categoria: this.$store.state.enquetes.selectedCategoria,
            }),
            this.getEnqueteEmExibicao({
              cod_producao: this.$store.state.producoes.selectedProducao.cod_producao,
            }),
          ]);

          this.$emit('option-updated');
          this.originalTitle = this.dsc_titulo;

          const timeoutId = setTimeout(() => {
            this.isEditing = false;
            this.showSuccess = false;
            this.loading = false;
          }, 400);
          this.timeoutIds.push(timeoutId);
        }
      } catch (error) {
        this.$toast.error(error.response?.data?.msg || "Erro ao editar texto");
        this.dsc_titulo = this.originalTitle;
        this.loading = false;
        this.isEditing = false;
      }
    },
  },
  beforeDestroy() {
    this.timeoutIds.forEach(id => clearTimeout(id));
  },
  watch: {
    editFormData: {
      handler(newVal) {
        if (newVal && !this.isEditing) {
          this.dsc_titulo = newVal.dsc_titulo;
          this.originalTitle = newVal.dsc_titulo;
        }
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss" scoped>
.inline-edit-container {
  display: inline-flex;
  align-items: center;
  min-height: 32px;
  flex: 1;
  min-width: 0;
}

.option-display {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: var(--sc-radius-sm);
  transition: all 0.25s var(--sc-easing-standard);
  max-width: 100%;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: var(--sc-radius-sm);
    background: rgba(255, 255, 255, 0);
    transition: background 0.25s var(--sc-easing-standard);
  }

  &:hover {
    &::before {
      background: var(--sc-surface-tint-hover);
    }

    .edit-icon {
      opacity: 0.8;
      transform: scale(1);
    }
  }

  &:active {
    transform: scale(0.98);
  }
}

.option-title {
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--v-text_primary-base);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  position: relative;
  z-index: 1;
}

.edit-icon {
  opacity: 0;
  transform: scale(0.8);
  transition: all 0.25s var(--sc-easing-standard);
  color: var(--v-text_secondary-base);
  flex-shrink: 0;
  position: relative;
  z-index: 1;
}

.option-edit {
  display: inline-flex;
  align-items: center;
  gap: 8px;

  &.is-saving .input-wrapper .option-input {
    opacity: 0.7;
  }

  &.save-success .input-wrapper .input-border {
    border-color: var(--v-primary-base);
    box-shadow: 0 0 0 3px rgba(var(--sc-color-primary-rgb),0.25);
  }
}

.input-wrapper {
  position: relative;
  display: inline-flex;
}

.option-input {
  background: var(--sc-surface-tint);
  border: none;
  border-radius: var(--sc-radius-sm);
  padding: 5px 10px;
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--v-text_primary-base);
  outline: none;
  transition: all 0.2s var(--sc-easing-standard);
  font-family: inherit;
  position: relative;
  z-index: 1;
  width: 200px;
  max-width: 100%;

  &:disabled {
    cursor: not-allowed;
  }
}

.input-border {
  position: absolute;
  inset: 0;
  border: 2px solid var(--v-primary-base);
  border-radius: var(--sc-radius-sm);
  pointer-events: none;
  transition: all 0.3s var(--sc-easing-standard);
  animation: border-pulse 0.35s var(--sc-easing-standard);
}

@keyframes border-pulse {
  0% {
    transform: scale(0.95);
    opacity: 0;
  }
  50% {
    transform: scale(1.02);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.status-icon-container {
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loading-indicator {
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.success-icon {
  animation: success-pop 0.35s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

@keyframes success-pop {
  0% {
    opacity: 0;
    transform: scale(0);
  }
  60% {
    transform: scale(1.15);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

/* Main transition */
.edit-transition-enter-active {
  animation: edit-in 0.22s var(--sc-easing-standard);
}

.edit-transition-leave-active {
  animation: edit-out 0.18s var(--sc-easing-standard);
}

@keyframes edit-in {
  0% {
    opacity: 0;
    transform: translateY(-6px) scale(0.96);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes edit-out {
  0% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  100% {
    opacity: 0;
    transform: translateY(6px) scale(0.96);
  }
}

</style>
